import ExploreOffIcon from "@mui/icons-material/ExploreOff";
import HomeIcon from "@mui/icons-material/Home";
import { Box, Button, Typography, useColorScheme } from "@mui/joy";
import { useNavigate } from "react-router-dom";

export default function NotFoundPage() {
  const { mode } = useColorScheme();
  const isDark = mode === "dark";
  const navigate = useNavigate();

  const accentColor = "rgba(239, 68, 68, 0.8)"; // Red for warning/lost

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: isDark
          ? "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #2e1a1a 100%)"
          : "linear-gradient(135deg, #fef2f2 0%, #fee2e2 50%, #fecaca 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        padding: "20px",
      }}
    >
      {/* Background magical effects */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(239, 68, 68, 0.12) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(251, 146, 60, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(254, 202, 202, 0.06) 0%, transparent 50%)
          `,
          zIndex: 0,
        }}
      />

      {/* Floating particles - more scattered/lost looking */}
      {[...Array(10)].map((_, i) => (
        <Box
          key={`particle-${i}`}
          sx={{
            position: "absolute",
            width: `${4 + (i % 3) * 2}px`,
            height: `${4 + (i % 3) * 2}px`,
            borderRadius: "50%",
            background: `radial-gradient(circle, rgba(239, 68, 68, 0.5) 0%, transparent 70%)`,
            left: `${5 + i * 9}%`,
            top: `${10 + (i % 7) * 12}%`,
            animation: `drift-${i % 4} ${10 + (i % 5)}s linear infinite`,
            animationDelay: `${i * 0.7}s`,
            "@keyframes drift-0": {
              "0%": { transform: "translate(0, 0) rotate(0deg)", opacity: 0.6 },
              "25%": {
                transform: "translate(30px, -20px) rotate(90deg)",
                opacity: 0.3,
              },
              "50%": {
                transform: "translate(-10px, 40px) rotate(180deg)",
                opacity: 0.6,
              },
              "75%": {
                transform: "translate(-30px, 10px) rotate(270deg)",
                opacity: 0.3,
              },
              "100%": {
                transform: "translate(0, 0) rotate(360deg)",
                opacity: 0.6,
              },
            },
            "@keyframes drift-1": {
              "0%": { transform: "translate(0, 0)", opacity: 0.4 },
              "50%": { transform: "translate(-40px, -30px)", opacity: 0.7 },
              "100%": { transform: "translate(0, 0)", opacity: 0.4 },
            },
            "@keyframes drift-2": {
              "0%": { transform: "translate(0, 0)", opacity: 0.5 },
              "33%": { transform: "translate(25px, 35px)", opacity: 0.2 },
              "66%": { transform: "translate(-35px, -15px)", opacity: 0.6 },
              "100%": { transform: "translate(0, 0)", opacity: 0.5 },
            },
            "@keyframes drift-3": {
              "0%": { transform: "translate(0, 0) scale(1)", opacity: 0.6 },
              "50%": {
                transform: "translate(20px, -25px) scale(0.5)",
                opacity: 0.3,
              },
              "100%": { transform: "translate(0, 0) scale(1)", opacity: 0.6 },
            },
          }}
        />
      ))}

      {/* Main content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          textAlign: "center",
          maxWidth: "600px",
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "140px",
              height: "140px",
              borderRadius: "50%",
              background: isDark
                ? "rgba(239, 68, 68, 0.1)"
                : "rgba(239, 68, 68, 0.12)",
              border: `2px solid ${accentColor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "wobble 4s ease-in-out infinite",
              "@keyframes wobble": {
                "0%, 100%": {
                  transform: "rotate(-3deg)",
                  boxShadow: `0 0 20px rgba(239, 68, 68, 0.2)`,
                },
                "50%": {
                  transform: "rotate(3deg)",
                  boxShadow: `0 0 30px rgba(239, 68, 68, 0.4)`,
                },
              },
            }}
          >
            <ExploreOffIcon
              sx={{
                fontSize: "4rem",
                color: accentColor,
              }}
            />
          </Box>
        </Box>

        {/* 404 Number */}
        <Typography
          sx={{
            fontSize: { xs: "5rem", md: "7rem" },
            fontFamily: "'Cinzel', serif",
            fontWeight: 700,
            color: isDark
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(30, 41, 59, 0.1)",
            lineHeight: 1,
            mb: -4,
            position: "relative",
            zIndex: -1,
          }}
        >
          404
        </Typography>

        {/* Title */}
        <Typography
          level="h1"
          sx={{
            color: isDark ? "white" : "#1e293b",
            fontFamily: "'Cinzel', serif",
            fontWeight: 400,
            letterSpacing: "0.08em",
            mb: 2,
            textShadow: isDark ? `0 0 30px rgba(239, 68, 68, 0.3)` : "none",
          }}
        >
          Path Not Found
        </Typography>

        {/* Subtitle */}
        <Typography
          level="body-lg"
          sx={{
            color: isDark
              ? "rgba(255, 255, 255, 0.7)"
              : "rgba(30, 41, 59, 0.7)",
            fontFamily: "'EB Garamond', serif",
            fontStyle: "italic",
            mb: 4,
            lineHeight: 1.8,
          }}
        >
          You have wandered beyond the charted lands of Osgara.
          <br />
          We're not sure what's here, but we're sure you'll tell us.
        </Typography>

        {/* Decorative line */}
        <Box
          sx={{
            width: "120px",
            height: "2px",
            background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
            margin: "0 auto 2rem",
          }}
        />

        {/* Home button */}
        <Button
          variant="outlined"
          startDecorator={<HomeIcon />}
          onClick={() => navigate("/")}
          sx={{
            background: "transparent",
            border: `1px solid ${accentColor}`,
            color: isDark ? "white" : "#1e293b",
            fontFamily: "'Cinzel', serif",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            px: 4,
            py: 1.5,
            transition: "all 0.3s ease",
            "&:hover": {
              background: `rgba(239, 68, 68, 0.1)`,
              border: `1px solid ${accentColor}`,
              transform: "translateY(-2px)",
              boxShadow: `0 4px 20px rgba(239, 68, 68, 0.3)`,
            },
          }}
        >
          Return to Known Lands
        </Button>
      </Box>
    </Box>
  );
}
